import debounce from '../utils/debounce';

const selectors = {
    siteHeader: 'shopify-section-site-header',
    drawer: '[data-drawer="search"]',
    closeButton: '[data-search-close-button]',
    searchInput: '[data-search-input]',
    overlay: '.overlay',
    submitSearchButton: '[data-search-submit-button]',
    submitSearchMobileButton: '[data-search-submit-mobile-button]',
    searchMobileInput: '[data-search-mobile-input]',
    searchMobilePopular: '[data-search-mobile-popular]',
    closeSearchMobileButton: '[data-search-mobile-close-button]',
    recommendedCollections: '.search__recommended_collections',
    predictiveResults: '[data-predictive-results]',
    predictiveResultsMobile: '[data-predictive-results-mobile]'
};

class Search {
    constructor(header) {
        this.siteHeader = document.getElementById(selectors.siteHeader);
        this.drawer = document.querySelector(selectors.drawer);
        this.closeButton = document.querySelector(selectors.closeButton);
        this.searchInput = document.querySelector(selectors.searchInput);
        this.overlay = document.querySelector(selectors.overlay);
        this.submitSearchButton = document.querySelector(selectors.submitSearchButton);
        this.submitSearchMobileButton = document.querySelector(selectors.submitSearchMobileButton);
        this.searchMobileInput = document.querySelector(selectors.searchMobileInput);
        this.searchMobilePopular = document.querySelector(selectors.searchMobilePopular);
        this.closeSearchMobileButton = document.querySelector(selectors.closeSearchMobileButton);
        this.predictiveResults = document.querySelector(selectors.predictiveResults);
        this.predictiveResultsMobile = document.querySelector(selectors.predictiveResultsMobile);

        this.closeButton.addEventListener('click', e => {
            e.preventDefault();
            this.resetDesktopSearch();
            header.closeDrawer(this.drawer);
        });

        this.searchInput.addEventListener('click', e => {
            e.preventDefault();
            this.submitSearchButton.setAttribute('data-state', 'visible');
        });

        this.searchMobileInput.addEventListener('focus', e => {
            e.preventDefault();
            this.submitSearchMobileButton.setAttribute('data-state', 'visible');
            this.searchMobilePopular.setAttribute('data-state', 'visible');
            this.closeSearchMobileButton.setAttribute('data-state', 'visible');
            this.overlay.style.setProperty('--overlay-z-index', 30);
            this.overlay.setAttribute('data-state', 'visible');
            document.body.classList.add('has-open-drawer');
        });

        this.searchInput.addEventListener(
            'input',
            debounce(event => this.handleSearchInput(event, this.predictiveResults), 300)
        );

        this.searchMobileInput.addEventListener(
            'input',
            debounce(event => this.handleSearchInput(event, this.predictiveResultsMobile), 300)
        );

        this.closeSearchMobileButton.addEventListener('click', e => {
            e.preventDefault();
            this.resetSearch(this.searchMobileInput, this.predictiveResultsMobile);
            this.submitSearchMobileButton.setAttribute('data-state', 'hidden');
            this.searchMobilePopular.setAttribute('data-state', 'hidden');
            this.closeSearchMobileButton.setAttribute('data-state', 'hidden');
            this.overlay.setAttribute('data-state', 'hidden');
            document.body.classList.remove('has-open-drawer');
        });

        this.overlay.addEventListener('click', e => {
            e.preventDefault();
            this.submitSearchMobileButton.setAttribute('data-state', 'hidden');
            this.searchMobilePopular.setAttribute('data-state', 'hidden');
            this.closeSearchMobileButton.setAttribute('data-state', 'hidden');
            this.overlay.setAttribute('data-state', 'hidden');
            document.body.classList.remove('has-open-drawer');
        });

        window.addEventListener('resize',  e => {
            e.preventDefault();
            header.closeDrawer(this.drawer);
            this.overlay.setAttribute('data-state', 'hidden');
            document.body.classList.remove('has-open-drawer');
        }, true);

    }

    clearResults(resultsContainer) {
        resultsContainer.replaceChildren();
        this.siteHeader.setAttribute('data-predictive-results', 'hidden');
    }

    resetSearch(searchInput, resultsContainer) {
        this.searchAbortController?.abort();
        searchInput.value = '';
        searchInput.setAttribute('aria-expanded', 'false');
        this.clearResults(resultsContainer);

        const recommendedCollections = searchInput
            .closest('form')
            ?.querySelector(selectors.recommendedCollections);

        if (recommendedCollections) recommendedCollections.hidden = false;
    }

    resetDesktopSearch() {
        this.resetSearch(this.searchInput, this.predictiveResults);
        this.submitSearchButton.setAttribute('data-state', 'hidden');
    }

    async handleSearchInput(event, resultsContainer) {
        const query = event.target.value.trim();
        const recommendedCollections = event.target
            .closest('form')
            ?.querySelector(selectors.recommendedCollections);

        this.searchAbortController?.abort();

        if (!query) {
            event.target.setAttribute('aria-expanded', 'false');
            this.clearResults(resultsContainer);
            if (recommendedCollections) recommendedCollections.hidden = false;
            return;
        }

        if (recommendedCollections) recommendedCollections.hidden = true;

        this.searchAbortController = new AbortController();

        const params = new URLSearchParams({
            q: query,
            section_id: 'predictive-results',
            'resources[type]': 'query,product,collection',
            'resources[limit]': '6',
            'resources[limit_scope]': 'each',
            'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor',
        });

        const root = window.Shopify?.routes?.root || '/';

        try {
            const response = await fetch(`${root}search/suggest?${params}`, {
                signal: this.searchAbortController.signal,
            });

            if (!response.ok) {
                throw new Error(`Predictive search failed: ${response.status}`);
            }

            const html = await response.text();

            if (event.target.value.trim() !== query) return;

            resultsContainer.innerHTML = html;
            event.target.setAttribute('aria-expanded', 'true');
            this.siteHeader.setAttribute('data-predictive-results', 'visible');
        } catch (error) {
            if (error.name === 'AbortError') return;
            console.error(error);
            event.target.setAttribute('aria-expanded', 'false');
            this.clearResults(resultsContainer);
        }
    }
}

export default Search;
